import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Armchair } from 'lucide-react';
import {
  ApiError,
  api,
  canManageUsers,
  type AuthUser,
  type TransferSeatColor,
  type TransferWindowRow,
} from '@/lib/api';
import { SEAT_COLOR_STYLE } from '@/lib/seatColor';

export default function AdminTransferWindows({ user }: { user: AuthUser }) {
  const [showClosed, setShowClosed] = useState(false);
  const listQuery = useQuery({
    queryKey: ['transfer-windows'],
    queryFn: api.listTransferWindows,
  });

  if (!canManageUsers(user.role)) {
    return <div className="text-slate-400">Only R5 can manage transfer windows.</div>;
  }

  const rows = listQuery.data ?? [];
  const open = rows.filter((r) => !r.closedAt);
  const closed = rows.filter((r) => r.closedAt);
  const shown = showClosed ? [...open, ...closed] : open;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Transfer Windows</h1>
          <p className="text-sm text-slate-400">
            Windows during which players from other servers can transfer to us. Seat counts are
            optional at creation.
          </p>
        </div>
        {closed.length > 0 && (
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-400">
            <input
              type="checkbox"
              checked={showClosed}
              onChange={(e) => setShowClosed(e.target.checked)}
            />
            Show closed ({closed.length})
          </label>
        )}
      </header>
      <CreateForm />
      <section className="grid gap-3 md:grid-cols-2">
        {listQuery.isLoading && <div className="text-slate-500">Loading…</div>}
        {rows.length === 0 && !listQuery.isLoading && (
          <div className="rounded-lg border border-dashed border-slate-800 p-6 text-center text-slate-500">
            No transfer windows yet.
          </div>
        )}
        {!listQuery.isLoading && rows.length > 0 && shown.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-800 p-6 text-center text-slate-500">
            No open windows. Tick "Show closed" above to see the archive.
          </div>
        )}
        {shown.map((w) => <WindowCard key={w.id} row={w} />)}
      </section>
      <Style />
    </div>
  );
}

function CreateForm() {
  const [name, setName] = useState('');
  const [seats, setSeats] = useState({ g: '', p: '', b: '', w: '' });
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const parseSeat = (v: string): number | null => {
    if (v.trim() === '') return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return null;
    return n;
  };

  const create = useMutation({
    mutationFn: () =>
      api.createTransferWindow({
        name: name.trim(),
        notes: notes.trim() || null,
        seatsGold: parseSeat(seats.g),
        seatsPurple: parseSeat(seats.p),
        seatsBlue: parseSeat(seats.b),
        seatsWhite: parseSeat(seats.w),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer-windows'] });
      qc.invalidateQueries({ queryKey: ['transfer-windows', 'open'] });
      setName('');
      setSeats({ g: '', p: '', b: '', w: '' });
      setNotes('');
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">
        Create window
      </h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          create.mutate();
        }}
        className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,90px))_auto]"
      >
        <input
          className="input"
          placeholder="Window name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <SeatInput
          label="Gold"
          value={seats.g}
          onChange={(v) => setSeats((s) => ({ ...s, g: v }))}
        />
        <SeatInput
          label="Purple"
          value={seats.p}
          onChange={(v) => setSeats((s) => ({ ...s, p: v }))}
        />
        <SeatInput
          label="Blue"
          value={seats.b}
          onChange={(v) => setSeats((s) => ({ ...s, b: v }))}
        />
        <SeatInput
          label="White"
          value={seats.w}
          onChange={(v) => setSeats((s) => ({ ...s, w: v }))}
        />
        <button className="btn-primary" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
        <textarea
          className="input md:col-span-6"
          placeholder="Notes (optional)"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </form>
      {error && <div className="text-sm text-red-400">{error}</div>}
    </section>
  );
}

function SeatInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-slate-400">
      <span>{label}</span>
      <input
        className="input"
        inputMode="numeric"
        placeholder="—"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
      />
    </label>
  );
}

function WindowCard({ row }: { row: TransferWindowRow }) {
  const closed = row.closedAt != null;
  const seats: { key: TransferSeatColor; value: number | null }[] = [
    { key: 'gold', value: row.seatsGold },
    { key: 'purple', value: row.seatsPurple },
    { key: 'blue', value: row.seatsBlue },
    { key: 'white', value: row.seatsWhite },
  ];
  return (
    <Link
      to={`/admin/transfers/${row.id}`}
      className={
        closed
          ? 'block rounded-lg border border-slate-800 bg-slate-900/20 p-4 opacity-70 transition hover:border-slate-700 hover:opacity-100'
          : 'block rounded-lg border border-slate-800 bg-slate-900/40 p-4 transition hover:border-slate-700 hover:bg-slate-900/70'
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-base font-semibold">{row.name}</h3>
            {closed && (
              <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-400">
                Closed
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            {seats.map(({ key, value }) => {
              const s = SEAT_COLOR_STYLE[key];
              return (
                <span key={key} className="inline-flex items-center gap-1">
                  <Armchair size={12} className={s.chair} />
                  <span className="tabular-nums text-slate-300">{value ?? '—'}</span>
                </span>
              );
            })}
          </div>
        </div>
        <div className="shrink-0 text-right text-xs text-slate-400">
          <div>{row.counts.total} players</div>
          <div className="text-slate-500">
            {row.counts.confirmed} conf · {row.counts.accepted} acc
          </div>
        </div>
      </div>
      {row.notes && <p className="mt-2 line-clamp-2 text-sm text-slate-400">{row.notes}</p>}
    </Link>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
        width: 100%;
      }
      .input:focus { outline: none; border-color: rgb(100 116 139 / 1); }
      .btn-primary {
        background: rgb(79 70 229 / 1);
        color: white;
        padding: 6px 12px;
        font-size: 14px;
        border-radius: 6px;
      }
      .btn-primary:hover { background: rgb(99 102 241 / 1); }
      .btn-primary:disabled { opacity: 0.5; }
    `}</style>
  );
}
